/**
 * แปลงภาพสลิปเป็นคำสั่ง ESC/POS
 *
 * ★ ทำไมแปลงที่เบราว์เซอร์ ไม่ใช่บน Android ★
 *
 * ส่วนที่พลาดง่ายที่สุดของการพิมพ์คือการอัดพิกเซลเป็นบิต — ผิดไปบิตเดียว
 * สลิปออกมาเป็นลายพร่า ๆ หรือเอียงทั้งใบ และกว่าจะรู้ก็ต่อเมื่อกระดาษออกมาแล้ว
 *
 * โค้ดที่รันบนเครื่อง Android คอมไพล์ทดสอบได้ยากกว่ามาก ส่วนโค้ดตรงนี้
 * ทดสอบได้ทุกบิตด้วย vitest — จึงย้ายงานยากมาไว้ฝั่งที่ตรวจสอบได้
 * เหลือให้ฝั่ง Android แค่ "รับไบต์แล้วเขียนลงซ็อกเก็ตบลูทูธ" ซึ่งเขียนผิดยาก
 *
 * ★ เครื่องพิมพ์ทั้งสองสาขาต่อผ่านบลูทูธเหมือนกัน ★
 * สาขา 2 เป็นเครื่องพิมพ์ในตัวของ Sunmi ที่โผล่เป็นอุปกรณ์บลูทูธชื่อ InnerPrinter
 * (ยืนยันจากหน้าตั้งค่าของเครื่องจริง) จึงไม่ต้องใช้ SDK เฉพาะของ Sunmi เลย
 */

/** ข้อมูลภาพแบบเดียวกับที่ canvas คืนมา — แยกไทป์ไว้เพื่อให้ทดสอบได้โดยไม่ต้องมี canvas */
export interface RasterSource {
  readonly data: Uint8ClampedArray | number[];
  readonly width: number;
  readonly height: number;
}

/**
 * ค่าตัดขาวดำ
 *
 * สลิปเป็นตัวหนังสือดำบนพื้นขาวล้วนอยู่แล้ว จึงตัดที่ค่ากลางตรง ๆ ได้
 * ไม่ต้อง dither — การ dither ตัวหนังสือทำให้วรรณยุกต์ไทยเส้นบาง ๆ ขาดหาย
 * ซึ่งเป็นเรื่องที่วัดไว้แล้วใน docs/font-spec.md
 */
const THRESHOLD = 128;

/**
 * อัดภาพเป็นบิตแบบ 1 บิตต่อจุด เรียงทีละแถว
 *
 * บิตสูงสุดของไบต์คือจุดซ้ายสุด และ ★ 1 = จุดดำ ★ (ตรงข้ามกับภาพขาวดำทั่วไป)
 * ความกว้างที่ไม่ลงตัว 8 จะถูกเติมด้วยจุดขาวทางขวา
 */
export function packRaster(image: RasterSource, threshold = THRESHOLD): {
  bytes: Uint8Array;
  bytesPerRow: number;
} {
  const { width, height } = image;
  if (width <= 0 || height <= 0) {
    throw new Error(`ขนาดภาพไม่ถูกต้อง ${width}×${height}`);
  }

  const bytesPerRow = Math.ceil(width / 8);
  const bytes = new Uint8Array(bytesPerRow * height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const at = (y * width + x) * 4;
      const r = image.data[at] ?? 255;
      const g = image.data[at + 1] ?? 255;
      const b = image.data[at + 2] ?? 255;
      const alpha = image.data[at + 3] ?? 255;

      // จุดโปร่งใสถือเป็นสีขาว ไม่ใช่สีดำ — ไม่งั้นพื้นที่ว่างจะกลายเป็นแถบดำทึบ
      const luminance = alpha === 0
        ? 255
        : (0.299 * r + 0.587 * g + 0.114 * b);

      if (luminance < threshold) {
        bytes[y * bytesPerRow + (x >> 3)]! |= 0x80 >> (x & 7);
      }
    }
  }

  return { bytes, bytesPerRow };
}

/* ══ คำสั่ง ESC/POS ═══════════════════════════════════════ */

const ESC = 0x1b;
const GS = 0x1d;

/** ล้างสถานะเครื่องพิมพ์ก่อนเริ่มงานใหม่ — กันค่าที่ค้างจากงานก่อนหน้า */
export const CMD_INIT = new Uint8Array([ESC, 0x40]);

/** เลื่อนกระดาษ n บรรทัด — เว้นที่ให้มือฉีกกระดาษ */
export const feed = (lines: number) => new Uint8Array([ESC, 0x64, Math.max(0, Math.min(255, lines))]);

/**
 * ★ เตะลิ้นชักเก็บเงิน ★
 *
 * ลิ้นชักต่ออยู่กับพอร์ต 24V ที่ตัวเครื่องพิมพ์ ไม่ได้ต่อกับเครื่อง POS
 * จึงสั่งได้ทางเดียวคือผ่านเครื่องพิมพ์
 *
 * ESC p m t1 t2 — m=0 คือขาที่ 2 · t1/t2 คือความยาวพัลส์หน่วยละ 2 มิลลิวินาที
 * ค่า 25/250 เป็นค่าที่ใช้กันทั่วไปและเป็นค่าที่แรงพอสำหรับลิ้นชักส่วนใหญ่
 */
export const CMD_DRAWER_KICK = new Uint8Array([ESC, 0x70, 0x00, 25, 250]);

/**
 * ★ ตัดกระดาษ ★
 *
 * GS V m — m=1 คือตัดแบบเหลือติ่งไว้ตรงกลาง
 * เลือกแบบเหลือติ่งเพราะใบครัวจะห้อยค้างไว้ให้คนหยิบ ไม่ร่วงลงพื้น
 * และเป็นคำสั่งที่เครื่องพิมพ์ความร้อนแทบทุกรุ่นรองรับ
 *
 * ★ ต้องเลื่อนกระดาษก่อนตัดเสมอ ★
 * ใบมีดอยู่เหนือหัวพิมพ์ราวหนึ่งถึงสองเซนติเมตร ถ้าสั่งตัดทันทีที่พิมพ์เสร็จ
 * รอยตัดจะกินบรรทัดสุดท้ายของสลิปหายไป
 */
export const CMD_CUT = new Uint8Array([GS, 0x56, 0x01]);

/**
 * แถวสูงสุดต่อหนึ่งคำสั่งพิมพ์ภาพ
 *
 * เครื่องพิมพ์หลายรุ่นมีบัฟเฟอร์จำกัด ส่งภาพยาว ๆ ทีเดียวแล้วมันตัดกลางคัน
 * จึงหั่นเป็นท่อน ๆ — สลิปจะต่อกันสนิทเพราะกระดาษเดินต่อเนื่องอยู่แล้ว
 */
const MAX_ROWS_PER_CHUNK = 128;

/**
 * ภาพหนึ่งใบเป็นชุดคำสั่ง GS v 0
 *
 * รูปแบบ: GS v 0 m xL xH yL yH d1…dk
 *   m  = 0 ขนาดปกติ
 *   x  = จำนวน "ไบต์" ต่อแถว (ไม่ใช่จำนวนจุด) — จุดที่พลาดกันบ่อยที่สุด
 *   y  = จำนวนแถวของท่อนนี้
 */
export function rasterCommands(image: RasterSource, threshold = THRESHOLD): Uint8Array {
  const { bytes, bytesPerRow } = packRaster(image, threshold);
  const parts: Uint8Array[] = [];

  for (let top = 0; top < image.height; top += MAX_ROWS_PER_CHUNK) {
    const rows = Math.min(MAX_ROWS_PER_CHUNK, image.height - top);
    const header = new Uint8Array([
      GS, 0x76, 0x30, 0x00,
      bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff,
      rows & 0xff, (rows >> 8) & 0xff,
    ]);
    parts.push(header, bytes.subarray(top * bytesPerRow, (top + rows) * bytesPerRow));
  }

  return concat(parts);
}

/**
 * งานพิมพ์หนึ่งใบครบชุด — ล้างสถานะ พิมพ์ภาพ เลื่อนกระดาษ แล้วตัด
 *
 * สั่งตัดเป็นค่าตั้งต้น เพราะทุกใบที่ระบบนี้พิมพ์คือใบที่ต้องแยกออกจากกัน
 * ใบครัวคนละครัวต้องแยกใบ ใบเสร็จต้องยื่นให้ลูกค้า
 * เครื่องที่ไม่มีใบมีดจะไม่สนใจคำสั่งนี้เอง ส่งไปก็ไม่เสียหาย
 */
export function printJobBytes(
  image: RasterSource,
  options: { feedLines?: number; cut?: boolean } = {},
): Uint8Array {
  const parts = [CMD_INIT, rasterCommands(image), feed(options.feedLines ?? 4)];
  if (options.cut !== false) parts.push(CMD_CUT);
  return concat(parts);
}

export function concat(parts: readonly Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/** แปลงเป็น base64 เพื่อส่งผ่าน JSON ไปให้ตัวพิมพ์ */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  // แบ่งเป็นท่อนเพราะ String.fromCharCode รับอาร์กิวเมนต์จำนวนมากไม่ไหว
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return btoa(binary);
}

/** ดึงข้อมูลภาพจาก canvas แล้วแปลงเป็นคำสั่งพิมพ์ */
export function canvasToPrintBytes(canvas: HTMLCanvasElement): Uint8Array {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('อ่านภาพจาก canvas ไม่ได้');
  return printJobBytes(ctx.getImageData(0, 0, canvas.width, canvas.height));
}
